import {
  Component,
  OnInit,
  Input,
  ViewChild,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { CmsPaginationService } from 'src/app/shared/services/cms-pagination.service';
import { AppConfig } from 'src/app/app-config';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { UntypedFormControl } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { IContent } from '../../core/models/i-content';
import { NotificationBarComponent } from 'src/app/shared/notification-bar/notification-bar.component';
import { SelectionModel } from '@angular/cdk/collections';
import {
  IFilterContentParams,
  FilterContentParams,
} from '../../core/models/i-filter-content';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { ContentService } from '../shared/services/cmsapi/content.service';
import { ContentListStateService } from '../shared/services/cmsapi/content-list-state.service';

import { WithProcessing } from 'src/app/shared/mixins/with-processing';
import { ContentType } from 'src/app/content-management/shared/enums/content-type';
import { Features } from 'src/app/core/feature-flags/features';
import { ContentStatus } from 'src/app/shared/enums/content-status';
import { ContentTypeRouterPaths } from 'src/app/shared/enums/content-type-router-paths';
import { ContentTypes } from 'src/app/shared/enums/content-types';
import { FeatureFlagsService } from 'src/app/core/feature-flags/feature-flag.service';

class AddContentListComponentBase {
  constructor() {}
}

const AddContentListComponentMixinBase = WithProcessing(
  AddContentListComponentBase
);

@Component({
  selector: 'app-add-content-list',
  templateUrl: './add-content-list.component.html',
  providers: [
    { provide: CmsPaginationService, useClass: CmsPaginationService },
  ],
})
export class AddContentListComponent
  extends AddContentListComponentMixinBase
  implements OnInit, OnDestroy
{
  @Input() contentListCode: string;
  @Input() deliveryTargetCodes: string[] = [];
  @Output() triggerPinAndAddToContentList = new EventEmitter<IContent[]>();
  @Output() triggerAddToContentList = new EventEmitter<IContent[]>();

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  private pageSize = 10;
  contentDataSource: MatTableDataSource<IContent> = new MatTableDataSource();
  totalArticles: number;
  displayedColumns: string[] = ['select', 'title', 'pin', 'add'];
  pagingOptions: number[] = [10, 25, 50, 100];
  contentResultIndex = 1;
  pageSizeFilter = new UntypedFormControl(this.pageSize);
  pageSubscriptions: Subscription[] = [];
  contentSelection = new SelectionModel<IContent>(true, []);
  query = new FilterContentParams(0, this.appConfig.contentResultLimit);
  totalContentCount: number;

  constructor(
    private contentService: ContentService,
    public cmsPaginationService: CmsPaginationService,
    private appConfig: AppConfig,
    public notificationBar: MatSnackBar,
    private contentListStateService: ContentListStateService,
    private dialogService: DialogService,
    private featureFlagsService: FeatureFlagsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.paginator.pageSize = this.pageSize;
    const pageSizeFilterSubscription =
      this.pageSizeFilter.valueChanges.subscribe((value) => {
        this.cmsPaginationService.selectPageSize(this.contentDataSource, value);
      });

    this.loadContent();
    this.pageSubscriptions.push(pageSizeFilterSubscription);
  }

  loadContent(
    filterParams: IFilterContentParams = new FilterContentParams(
      0,
      this.appConfig.contentResultLimit
    )
  ): void {
    const getFilteredContent$ = this.contentService.getFiltered$(
      this.deliveryTargetCodes,
      this.contentListCode,
      filterParams
    );

    const tableSubscription = this.process$(getFilteredContent$).subscribe(
      (content) => {
        this.contentDataSource.data = content.articles;
        this.contentDataSource.paginator = this.paginator;
        this.contentDataSource.sort = this.sort;
        this.totalContentCount = content.totalCount;
        this.cmsPaginationService.populatePageButtonArray(
          this.contentDataSource
        );
      },
      (error) => {
        this.dialogService.openErrorDialog({
          message:
            'An error has occurred whilst loading the articles for this content list. Please contact IT.',
        });
      }
    );

    this.pageSubscriptions.push(tableSubscription);
  }

  goToNextPage(contentDataSource: MatTableDataSource<IContent>): void {
    if (contentDataSource.paginator.hasNextPage()) {
      this.cmsPaginationService.nextPage(contentDataSource);
    } else if (this.contentListStateService.isContentListChanged()) {
      this.notificationBar.openFromComponent(NotificationBarComponent, {
        data: 'You need to save your current changes before continuing to the next page',
      });
    } else {
      this.contentResultIndex++;
      this.query.pageSize =
        this.contentResultIndex * this.appConfig.contentResultLimit;
      this.loadContent(this.query);
      (contentDataSource.paginator.pageIndex as number)++;
    }
  }

  isSelectAllCheckboxEnabled(): boolean {
    return (
      this.contentDataSource.data.filter(
        (content) => !content.isPinned || !content.isInContentList
      ).length !== 0
    );
  }

  isAllSelected(
    contentSelection: SelectionModel<IContent>,
    contentDataSource: MatTableDataSource<IContent>
  ): boolean {
    const numSelected = contentSelection.selected.length;
    const numRows = contentDataSource.data.filter(
      (content) => !content.isPinned || !content.isInContentList
    ).length;
    return numSelected === numRows && contentSelection.hasValue();
  }

  masterToggle(
    contentSelection: SelectionModel<IContent>,
    contentDataSource: MatTableDataSource<IContent>
  ): void {
    const isAllSelected = this.isAllSelected(
      contentSelection,
      contentDataSource
    );
    contentSelection.clear();
    if (!isAllSelected) {
      contentDataSource.data
        .filter((content) => !content.isPinned || !content.isInContentList)
        .forEach((content) => contentSelection.select(content));
    }
  }

  isContentSelected(content: IContent): boolean {
    return this.contentSelection.selected.some(
      (x) => x.contentId === content.contentId
    );
  }

  allSelectedContentAddedToList(
    contentSelection: SelectionModel<IContent>
  ): boolean {
    // tslint:disable-next-line:prefer-for-of
    for (let index = 0; index < contentSelection.selected.length; index++) {
      if (!contentSelection.selected[index].isInContentList) {
        return false;
      }
    }
    return true;
  }

  filter(filterValues: IFilterContentParams): void {
    if (this.contentListStateService.isContentListChanged()) {
      this.notificationBar.openFromComponent(NotificationBarComponent, {
        data: 'You need to save your current changes before continuing',
      });
      return;
    }
    this.query = new FilterContentParams(
      0,
      this.appConfig.contentResultLimit,
      filterValues.headline,
      filterValues.authors,
      filterValues.publishDateFrom,
      filterValues.publishDateTo,
      filterValues.contentListId
    );

    this.loadContent(this.query);
    this.contentSelection.clear();
  }

  allDataLoadedAndDisplayed(listHasNextPage: boolean): boolean {
    return this.totalContentCount < this.query.pageSize && !listHasNextPage;
  }

  addToContentList(contents: IContent[]): void {
    if (
      this.contentSelection.selected.length &&
      this.allSelectedContentAddedToList(this.contentSelection)
    ) {
      this.triggerAddToContentList.emit([]);
      return;
    }
    this.triggerAddToContentList.emit(
      contents.filter((content) => !content.isInContentList)
    );
    contents.forEach((content) => {
      content.isInContentList = true;
    });
    this.contentSelection.clear();
  }

  pinAndAddToContentList(contents: IContent[]): void {
    this.triggerPinAndAddToContentList.emit(contents);
    contents.forEach((content) => {
      content.isInContentList = true;
    });
    this.contentSelection.clear();
  }

  isEnabledForContentType$(contentType: ContentType): Observable<boolean> {
    return this.featureFlagsService.isEnabledForContentType$(contentType);
  }

  getFeatureForContentType(contentType: ContentType) {
    return this.featureFlagsService.getFeatureForContentType(contentType);
  }

  getRouteForContentType(
    contentType: string,
    contentId: string,
    state: ContentStatus
  ) {
    return this.featureFlagsService.getRouteForContentType(
      contentType,
      contentId,
      state
    );
  }

  ngOnDestroy(): void {
    this.pageSubscriptions.forEach((subscription) =>
      subscription.unsubscribe()
    );
  }
}
